import { PhoneOutlined } from "@ant-design/icons";
import { Avatar, Divider } from "antd";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getDebtPaymentsThunk,
    getKpiGetProgressThunk,
    getKpiGetSalaryToPayThunk,
    getUserTeamGetByManagerThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";

import FlexContainer from "@/components/FlexContainer";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import Meta from "@/components/Meta";
import { PrimaryText, SecondaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { getKpiTargetDate } from "@/utils";

import { DashboardTab } from "./DashboardTab";
import {
    StyledMemberBadge,
    StyledMemberContainer,
    StyledMemberLogo,
} from "./Styled";

function MemberInfo() {
    const { team } = useAppSelector((state) => state.sv);
    const { id } = useAppSelector((state) => state.user);

    const [dashboardLoaded, setDashboardLoaded] = useState(false);

    const dispatch = useAppDispatch();
    const { userId } = useParams();
    const today = dayjs().format(backendDateFormat.date);

    const userInfo = useMemo(
        () => team?.members?.find((member) => member.userId === userId),
        [team?.members, userId],
    );

    const userStatusText = useMemo(() => {
        return userInfo?.isWorking
            ? `Почав о ${dayjs(userInfo?.startedAt).format("H:mm")}`
            : "Не на роботі";
    }, [userInfo]);

    const loadDashboardData = useCallback(async () => {
        try {
            const kpiTargetDate = getKpiTargetDate();
            await Promise.all([
                dispatch(getUserTeamGetByManagerThunk({ managerId: id })),
                dispatch(
                    getKpiGetSalaryToPayThunk({
                        employeeId: userId,
                        targetDate: kpiTargetDate,
                    }),
                ),
                dispatch(
                    getKpiGetProgressThunk({
                        employeeId: userId,
                        targetDate: kpiTargetDate,
                    }),
                ),
                dispatch(
                    getDebtPaymentsThunk({
                        agentId: userId,
                        targetDate: today,
                    }),
                ),
            ]);
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            setDashboardLoaded(true);
        }
    }, [dispatch, id, userId, today]);

    useEffect(() => {
        setDashboardLoaded(false);
        loadDashboardData();
    }, [userId, id, dispatch, loadDashboardData]);

    return (
        <>
            <Meta title="Work Plan" />
            <Header path="/team" titleLoading={!dashboardLoaded} />
            <MainContainer>
                <StyledMemberContainer>
                    <FlexContainer
                        justifyContent="flex-start"
                        alignItems="flex-start"
                        gap="16px"
                    >
                        <StyledMemberBadge dot isWorking={userInfo?.isWorking}>
                            <Avatar size={42} icon={<StyledMemberLogo />} />
                        </StyledMemberBadge>

                        <FlexContainer
                            flexDirection="column"
                            alignItems="flex-start"
                            gap="8px"
                            width="100%"
                        >
                            <PrimaryText>{userInfo?.fullName}</PrimaryText>
                            <SecondaryText>{userStatusText}</SecondaryText>
                            <FlexContainer
                                width="100%"
                                justifyContent="space-between"
                                gap="8px"
                            >
                                <SecondaryText
                                    as="a"
                                    href={`tel:${userInfo?.phone}`}
                                >
                                    {userInfo?.phone}
                                </SecondaryText>
                                <SecondaryText
                                    as="a"
                                    href={`tel:${userInfo?.phone}`}
                                >
                                    <PhoneOutlined />
                                </SecondaryText>
                            </FlexContainer>
                        </FlexContainer>
                    </FlexContainer>
                    <Divider style={{ margin: "16px 0" }} />
                    <DashboardTab isLoaded={dashboardLoaded} />
                </StyledMemberContainer>
            </MainContainer>
        </>
    );
}

export default MemberInfo;
